import { FC } from 'react';
import * as Card from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Mail, Calendar, Phone, AlertCircle, Clock, XCircle, CheckCircle } from 'lucide-react';
import type { AdminUser } from '@/integrations/supabase/user-types';
import { UserActions } from './UserActions';
import { Button } from '@/components/ui/button';
import ReactCountryFlag from "react-country-flag";
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface UserCardProps {
  user: AdminUser;
  onDelete: () => void;
}

export const UserCard: FC<UserCardProps> = ({ user, onDelete }) => {
  // Ensure country code is uppercase for proper flag display
  const normalizedCountryCode = user.country_code?.toUpperCase() || 'PE';

  const getStatusBadgeProps = (estado: string) => {
    switch (estado) {
      case 'Email no confirmado':
        return { 
          variant: 'destructive' as const, 
          children: 'Email no confirmado',
        };
      case 'Trial expirado':
        return { 
          variant: 'destructive' as const, 
          children: 'Trial expirado',
        };
      case 'Trial por expirar':
        return { 
          variant: 'secondary' as const, 
          children: 'Trial por expirar',
        };
      case 'En trial':
        return { 
          variant: 'secondary' as const, 
          children: 'En trial',
        };
      case 'Activo':
        return { 
          variant: 'default' as const, 
          children: 'Activo',
        };
      default:
        return { 
          variant: 'outline' as const, 
          children: estado || 'Estado desconocido',
        };
    }
  };

  const handleWhatsAppClick = () => {
    if (user.whatsapp && user.whatsapp !== 'No disponible') {
      const whatsappUrl = `https://wa.me/${user.whatsapp}`;
      window.open(whatsappUrl, '_blank');
    }
  };

  const handleEmailClick = () => {
    if (user.email) {
      window.location.href = `mailto:${user.email}`;
    }
  };

  return (
    <Card.Card className={`w-full hover:shadow-lg transition-shadow ${user.puede_eliminar ? 'border-red-200 bg-red-50' : ''}`}>
      <Card.CardHeader>
        <Card.CardTitle className="space-y-2">
          <div className="text-lg font-semibold truncate">
            {user.tutor_name || 'No disponible'}
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant={user.role === 'admin' ? "default" : "secondary"}>
              {user.role === 'admin' ? 'Admin' : 'Usuario'}
            </Badge>
            <Badge {...getStatusBadgeProps(user.estado)} />
            {user.puede_eliminar && (
              <Badge variant="destructive">
                Puede eliminarse
              </Badge>
            )}
          </div>
        </Card.CardTitle>
      </Card.CardHeader>
      <Card.CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm text-gray-600 flex items-center gap-2">
              <Mail className="h-4 w-4 shrink-0" />
              <span className="break-all">{user.email || 'No disponible'}</span>
              {user.dias_sin_confirmar > 0 && (
                <Badge variant="outline" className="ml-auto">
                  <XCircle className="h-3 w-3 mr-1 text-red-500" />
                  {user.dias_sin_confirmar}d sin confirmar
                </Badge>
              )}
            </p>
            <p className="text-sm text-gray-600 flex items-center gap-2">
              <Phone className="h-4 w-4 shrink-0" />
              <span>{user.whatsapp || 'No disponible'}</span>
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-semibold">Estudiante:</span>{' '}
              {user.student_name || 'No disponible'}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-semibold">Grupo:</span>{' '}
              {user.student_age_group || 'No disponible'}
            </p>
            <p className="text-sm text-gray-600 flex items-center gap-2">
              <ReactCountryFlag 
                countryCode={normalizedCountryCode}
                svg 
                style={{
                  width: '1em',
                  height: '1em',
                }}
                title={normalizedCountryCode}
              />
              <span>{normalizedCountryCode}</span>
            </p>
          </div>

          <div className="space-y-2 pt-2 border-t">
            <h4 className="font-semibold text-sm">Estado de la cuenta</h4>
            <div className="space-y-1">
              {/* Mostrar días desde creación */}
              <p className="text-sm text-gray-600 flex items-center gap-2">
                <Calendar className="h-4 w-4 shrink-0" />
                <span>Creada hace {user.dias_desde_creacion} días</span>
              </p>

              {/* Estado de confirmación de email */}
              <p className="text-sm text-gray-600 flex items-center gap-2">
                {user.email_confirmed ? (
                  <>
                    <CheckCircle className="h-4 w-4 shrink-0 text-green-500" />
                    <span>Email confirmado</span>
                    {user.email_confirmed_at && (
                      <span className="text-xs text-gray-500 ml-2">
                        ({format(new Date(user.email_confirmed_at), "d MMM, yyyy", { locale: es })})
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4 shrink-0 text-red-500" />
                    <span>Email sin confirmar ({user.dias_sin_confirmar} días)</span>
                  </>
                )}
              </p>

              {/* Trial */}
              {user.trial_ends_at && (
                <p className="text-sm text-gray-600 flex items-center gap-2">
                  <Clock className="h-4 w-4 shrink-0 text-blue-500" />
                  <span>
                    Trial {user.trial_active ? "activo" : "expirado"}: {format(new Date(user.trial_ends_at), "d 'de' MMMM, yyyy", { locale: es })}
                  </span>
                </p>
              )}

              {/* Período de gracia */}
              {user.in_grace_period && (
                <p className="text-sm text-gray-600 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 shrink-0 text-yellow-500" />
                  <span>
                    Período de gracia hasta: {format(new Date(user.grace_period_ends_at), "d 'de' MMMM, yyyy", { locale: es })}
                  </span>
                </p>
              )}

              {/* Período actual */}
              {user.current_period_ends_at && (
                <p className="text-sm text-gray-600 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 shrink-0 text-green-500" />
                  <span>
                    Suscripción activa hasta: {format(new Date(user.current_period_ends_at), "d 'de' MMMM, yyyy", { locale: es })}
                  </span>
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-4">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleWhatsAppClick}
              disabled={!user.whatsapp || user.whatsapp === 'No disponible'}
            >
              WhatsApp
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleEmailClick}
              disabled={!user.email}
            >
              Email
            </Button>
          </div>
          
          <UserActions user={user} onSuccess={onDelete} />
        </div>
      </Card.CardContent>
    </Card.Card>
  );
};